import React from 'react';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';

import { FooterProps } from './types';
import * as Styled from './styles';

import { LogoLyngenNorth } from '../../../assets/svg';
import Nav from '../../molecules/Nav';
import InfoTile from '../../molecules/InfoTile';
import Link from '../../atoms/Link';
import { LinkMode } from '../../atoms/Link/enums';
import Typography from '../../styles/Typography';
import Socials from '../../molecules/Socials';
import { useCookieConsentContext } from '../CookieConsent/context/CookieConsentContext';

const Footer: React.FC<FooterProps> = ({ data }) => {
  const {
    logoUrl: { to: logoUrl },
    menu,
    location,
    contact,
    terms,
    extractedButton,
    copyrights,
    socialsLabel,
    socials,
  } = data;
  const breakpoints = useBreakpoint();
  
  // Safely get cookie consent context
  let showSettings: (() => void) | undefined;
  try {
    const cookieContext = useCookieConsentContext();
    showSettings = cookieContext?.showSettings;
  } catch (error) {
    // Context not available, cookie settings won't work but footer will still render
    console.warn('Cookie consent context not available:', error);
  }

  const handleCookieSettings = (e: React.MouseEvent) => {
    e.preventDefault();
    if (showSettings) {
      showSettings();
    }
  };

  return (
    <Styled.Footer>
      <Styled.FooterTop
        pt={breakpoints.tablet ? '6.75rem' : '4rem'}
        pb={breakpoints.tablet ? '5rem' : '3rem'}
      >
        <Styled.FooteTopInner>
          <Styled.FooterLogo to={logoUrl} mode={LinkMode.HasPlainLook} aria-label="logo">
            <LogoLyngenNorth />
          </Styled.FooterLogo>
          <Styled.FooterMenu>
            {breakpoints.tablet && (
              <Styled.FooterNav>
                <Nav items={menu} isAlt />
              </Styled.FooterNav>
            )}
            {extractedButton && (
              <Styled.FooterAction>
                <Link to={extractedButton.link}>{extractedButton.label}</Link>
              </Styled.FooterAction>
            )}
          </Styled.FooterMenu>
          {location && (
            <Styled.FooterLocation>
              <InfoTile title={location.title} desc={location.desc} />
            </Styled.FooterLocation>
          )}
          {contact && (
            <Styled.FooterContact>
              <Styled.FooterContactList>
                {contact.map(({ title, desc, key }) => (
                  <Styled.FooterContactItem {...{ key }}>
                    <InfoTile {...{ title, desc }} />
                  </Styled.FooterContactItem>
                ))}
              </Styled.FooterContactList>
            </Styled.FooterContact>
          )}
        </Styled.FooteTopInner>
      </Styled.FooterTop>
      <Styled.FooterBottom pt={['2.5rem']} pb={breakpoints.tablet ? '5rem' : '2.5rem'}>
        <Styled.FooterBottomInner>
          <Styled.FooterTerms>
            {terms && (
              <>
                <Styled.FooterTermsLink to={terms.link} mode={LinkMode.HasPlainLook}>
                  <Typography variant="textS">{terms.label}</Typography>
                </Styled.FooterTermsLink>
                {showSettings && <span style={{ margin: '0 8px', fontSize: 'inherit' }}>|</span>}
              </>
            )}
            {showSettings && (
              <button 
                onClick={handleCookieSettings} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  padding: 0, 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'inherit',
                  fontSize: 'inherit',
                  fontFamily: 'inherit'
                }}
              >
                <Typography variant="textS">Cookie Settings</Typography>
              </button>
            )}
          </Styled.FooterTerms>
          {copyrights && (
            <Styled.FooterPrivacy>
              <Styled.FooterPrivacyDesc>
                <Typography variant="textS">{copyrights}</Typography>
              </Styled.FooterPrivacyDesc>
            </Styled.FooterPrivacy>
          )}
          {!!socials?.length && socialsLabel && (
            <Styled.FooterSocials>
              <Styled.FooterSocialsLabel variant="textS">{socialsLabel}</Styled.FooterSocialsLabel>
              <Styled.FooterSocialsList>
                <Socials items={socials} />
              </Styled.FooterSocialsList>
            </Styled.FooterSocials>
          )}
        </Styled.FooterBottomInner>
      </Styled.FooterBottom>
    </Styled.Footer>
  );
};

export default Footer;
